import React, { useState } from 'react';
import { Gift, Crown, Diamond, Heart, Star, Award, Sparkles, TrendingUp, Shield, Clock, Target, Eye, Gem } from 'lucide-react';

const Page1000Plus = ({ setCurrentPage }) => {
  const [activeCategory, setActiveCategory] = useState('all');

  const luxuryCategories = [
    {
      id: 'heirloom',
      name: 'Arvegaver',
      icon: Crown,
      color: 'purple',
      description: 'Tidløse skatter som går i arv',
      priceRange: '1500-25000+ kr'
    },
    {
      id: 'experiences',
      name: 'Drømmeopplevelser',
      icon: Heart,
      color: 'rose',
      description: 'Once-in-a-lifetime øyeblikk',
      priceRange: '2000-50000+ kr'
    },
    {
      id: 'bespoke',
      name: 'Skreddersydd luksus',
      icon: Sparkles,
      color: 'indigo',
      description: 'Unike kreasjoner bare for dem',
      priceRange: '3000-100000+ kr'
    },
    {
      id: 'investment',
      name: 'Investeringsgaver',
      icon: TrendingUp,
      color: 'emerald',
      description: 'Verdier som vokser over tid',
      priceRange: '5000-200000+ kr'
    }
  ];

  const luxuryProducts = {
    heirloom: [
      {
        category: 'Smykker & Klokker',
        icon: '💎',
        items: [
          'Forlovelsesringer med certificerede diamanter',
          'Vintage Rolex, Omega eller Cartier klokker',
          'Håndlagde smykker fra anerkjente gullsmeder',
          'Antikke familiesmykker restaurert til perfeksjon',
          'Signerte designersmykker (Tiffany, Bulgari)',
          'Personaliserte arvesmykker med gravering'
        ]
      },
      {
        category: 'Kunst & Kultur',
        icon: '🎨',
        items: [
          'Originale malerier fra etablerte kunstnere',
          'Limiterte opplag og signerte litografier',
          'Antikke møbler og designklassikere',
          'Sjeldne bøker og førsteutgaver',
          'Vintage instrumenter (Steinway piano, Stradivarius)',
          'Kulturelle artefakter og etnisk kunst'
        ]
      },
      {
        category: 'Samleobjekter',
        icon: '🏆',
        items: [
          'Vintage viner fra legendariske årganger',
          'Historiske mynter og edle metaller',
          'Sports memorabilia fra legender',
          'Klassiske biler og motorcykler',
          'Antikke våpen og militaria',
          'Sjeldne frimerker og dokumenter'
        ]
      }
    ],
    experiences: [
      {
        category: 'Luksusreiser',
        icon: '✈️',
        items: [
          'Private jet-reiser til eksotiske destinasjoner',
          'Overnatting i verdens beste hoteller (Ritz, Four Seasons)',
          'Safari i Afrika med private guider',
          'Yacht-charter i Middelhavet eller Karibia',
          'Nordlysreise med luksusopphold i Lapland',
          'Kulturreise til Japan med private opplevelser'
        ]
      },
      {
        category: 'Kulinariske Opplevelser',
        icon: '🍾',
        items: [
          'Michelin 3-stjernes restauranter rundt om i verden',
          'Private kokk-opplevelser hjemme',
          'Vin-degustasjon hos berømte vingårder',
          'Whisky-tasting med master destillatører',
          'Kulinarisk verdensomseilig med eksperter',
          'Private middager med celebrity-kokker'
        ]
      },
      {
        category: 'Eksklusive Opplevelser',
        icon: '🎭',
        items: [
          'VIP-konsertopplevelser og backstage-pass',
          'Private visninger i verdens beste museer',
          'Møte med favoritt-forfattere eller kunstnere',
          'Eksklusiv adgang til fashion shows',
          'Private yacht-regatta eller motorsport-events',
          'Helikopter-turer over ikoniske landskap'
        ]
      }
    ],
    bespoke: [
      {
        category: 'Personlig Kunst',
        icon: '🖼️',
        items: [
          'Commissioned portretter av anerkjente kunstnere',
          'Skreddersydde skulpturer for hjemmet',
          'Personlige dokumentarfilmer om deres liv',
          'Biografier skrevet av profesjonelle forfattere',
          'Slektshistorie profesjonelt forsket og dokumentert',
          'Private konserter med deres favoritartister'
        ]
      },
      {
        category: 'Hjem & Livsstil',
        icon: '🏰',
        items: [
          'Skreddersydd interiørdesign av anerkjente designere',
          'Hagedesign og landscape-arkitektur',
          'Smart home-systemer tilpasset deres behov',
          'Private bibliotek med håndvalgte bøker',
          'Vinlagere designet for deres samling',
          'Personlige spa og wellness-rom'
        ]
      },
      {
        category: 'Skreddersydde Produkter',
        icon: '👑',
        items: [
          'Haute couture klær fra Paris-ateliers',
          'Skreddersydde biler fra luksusmerker',
          'Håndlagde sko fra italienske mestre',
          'Personaliserte parfymer fra Grasse',
          'Tilpassede møbler av anerkjente designere',
          'Eksklusive smykker designet fra bunnen av'
        ]
      }
    ],
    investment: [
      {
        category: 'Finansielle Investeringer',
        icon: '📈',
        items: [
          'Blue-chip aksjer i deres navn',
          'Kryptovaluta-porteføljer med sikker lagring',
          'Eiendomsinvesteringer og REITs',
          'Commodity-investeringer (gull, sølv, platina)',
          'Hedge fund-andeler (kun kvalifiserte investorer)',
          'Private equity-investeringer'
        ]
      },
      {
        category: 'Alternative Investeringer',
        icon: '💼',
        items: [
          'Vintage viner som investering (Bordeaux, Bourgogne)',
          'Sjeldne whisky-flasker fra stengte destillerier',
          'Kunst som investering med verdigaranti',
          'Klassiske biler med dokumentert historie',
          'Sjeldne instrumenter (vintage gitarer, pianoer)',
          'Sports-kort og memorabilia med autentisitetsbevis'
        ]
      },
      {
        category: 'Langsiktige Verdier',
        icon: '🏛️',
        items: [
          'Utdanningsfond for barn og barnebarn',
          'Livsforsikringer med investeringskomponent',
          'Pensjonskontoer med luksus-tillegg',
          'Stiftelser i deres navn til veldedige formål',
          'Eiendommer som kan generere inntekt',
          'Intellectual property og patent-investeringer'
        ]
      }
    ]
  };

  const occasionGuides = [
    {
      occasion: 'Forlovelse & Bryllup',
      emoji: '💍',
      budget: '15000-100000+ kr',
      suggestions: [
        'Forlovelsesring med certificeret diamant',
        'Bryllupsreise til drømmedestinasjon',
        'Vintage champagne fra deres fødselsår',
        'Skreddersydd brudekjole fra haute couture'
      ]
    },
    {
      occasion: 'Store Jubileum',
      emoji: '🥂',
      budget: '10000-50000+ kr',
      suggestions: [
        'Reise til stedet de møttes første gang',
        'Kommissionert portrett av hele familien',
        'Vintage vin fra deres bryllupsår',
        'Fornyelse av ekteskapsløfter med fest'
      ]
    },
    {
      occasion: 'Milepæl-bursdager',
      emoji: '🎂',
      budget: '5000-30000+ kr',
      suggestions: [
        'Drømmeopplevelse de alltid har snakket om',
        'Investering i deres største lidenskap',
        'Luksusreise med hele familien',
        'Personalisert kunst eller smykke'
      ]
    },
    {
      occasion: 'Pensjonering',
      emoji: '🏆',
      budget: '8000-40000+ kr',
      suggestions: [
        'Verdensreise de har drømt om',
        'Hobby-investering (vintbodegod, kunstvleri)',
        'Luksus-oppgradering av hjemmet',
        'Minnesmerke over karrieren deres'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-purple-600 via-blue-600 to-indigo-700">
          <div className="absolute inset-0 bg-black opacity-20"></div>
        </div>
        
        {/* Floating luxury icons */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-1/4 text-6xl opacity-20 animate-float">💎</div>
          <div className="absolute top-32 right-1/4 text-5xl opacity-15 animate-float-delayed">👑</div>
          <div className="absolute bottom-32 left-1/3 text-7xl opacity-10 animate-float">⭐</div>
          <div className="absolute bottom-20 right-1/3 text-5xl opacity-25 animate-float-delayed">🏆</div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center text-white">
            <div className="mb-8">
              <Diamond className="w-20 h-20 mx-auto mb-6 text-yellow-300" />
            </div>
            <h1 className="text-6xl md:text-7xl font-extrabold mb-6 bg-gradient-to-r from-yellow-300 to-yellow-100 bg-clip-text text-transparent">
              Luksus-gaver
            </h1>
            <p className="text-2xl font-light mb-4">over 1000 kroner</p>
            <p className="text-xl mb-12 max-w-3xl mx-auto leading-relaxed opacity-90">
              Her begynner gavenes ekstralige. Dette er investeringer i kjærlighet, arveskatter for fremtiden, 
              og opplevelser som forandrer liv. Hver gave i dette segmentet forteller en historie som varer generasjoner.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => setCurrentPage('gift-finder')}
                className="px-8 py-4 bg-gradient-to-r from-yellow-400 to-yellow-300 text-gray-900 rounded-xl hover:from-yellow-300 hover:to-yellow-200 transition-all duration-300 transform hover:scale-105 shadow-2xl font-bold text-lg"
              >
                <Gift className="w-6 h-6 inline mr-3" />
                Oppdag luksus-gaver
              </button>
              <button
                onClick={() => setCurrentPage('categories')}
                className="px-8 py-4 border-2 border-white text-white rounded-xl hover:bg-white hover:text-gray-900 transition-all duration-300 font-semibold text-lg"
              >
                Se alle kategorier
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Luxury Philosophy */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Luksus-gaver: Mer enn bare pris
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                En virkelig luksus-gave måles ikke bare i kroner, men i betydning, kvalitet og evnen til å skape minner som varer livet ut.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
              <div className="text-center group">
                <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                  <Target className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Perfekt timing</h3>
                <p className="text-gray-600">
                  Luksus-gaver markerer livets mest betydningsfulle øyeblikk - ikke hverdagen, men milepælene som definerer hvem vi er.
                </p>
              </div>

              <div className="text-center group">
                <div className="w-20 h-20 bg-gradient-to-br from-rose-500 to-rose-600 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                  <Heart className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Dyp kjærlighet</h3>
                <p className="text-gray-600">
                  Disse gavene er forbeholdt de viktigste menneskene i livet ditt - de som har formet deg og som du vil være sammen med for alltid.
                </p>
              </div>

              <div className="text-center group">
                <div className="w-20 h-20 bg-gradient-to-br from-amber-500 to-amber-600 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                  <Award className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Historisk betydning</h3>
                <p className="text-gray-600">
                  En luksus-gave blir en del av familiehistorien - noe som huskes, snakkes om og kanskje til og med går i arv til fremtidige generasjoner.
                </p>
              </div>
            </div>

            <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-2xl p-8 border border-purple-100">
              <div className="flex items-start space-x-6">
                <div className="flex-shrink-0">
                  <Gem className="w-12 h-12 text-purple-600" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">Investeringer i relasjoner</h3>
                  <p className="text-lg text-gray-700 leading-relaxed">
                    Luksus-gaver er ikke utgifter - de er investeringer i de viktigste relasjonene i livet ditt. 
                    De kommuniserer kjærlighet, respekt og forpliktelse på en måte ord aldri kan. Når du velger å investere 
                    betydelige beløp i en gave, sender du et kraftfullt signal om hvor mye personen betyr for deg.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Category Explorer */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Utforsk luksuskategorier
              </h2>
              <p className="text-xl text-gray-600">
                Fire unike tilnærminger til luksus - hver med sitt eget formål og sin egen magi
              </p>
            </div>

            {/* Category Navigation */}
            <div className="flex flex-wrap justify-center gap-4 mb-12">
              <button
                onClick={() => setActiveCategory('all')}
                className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${
                  activeCategory === 'all'
                    ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-lg'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                Alle kategorier
              </button>
              {luxuryCategories.map((category) => {
                const IconComponent = category.icon;
                return (
                  <button
                    key={category.id}
                    onClick={() => setActiveCategory(category.id)}
                    className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 flex items-center space-x-2 ${
                      activeCategory === category.id
                        ? `bg-gradient-to-r from-${category.color}-600 to-${category.color}-700 text-white shadow-lg`
                        : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                    }`}
                  >
                    <IconComponent className="w-5 h-5" />
                    <span>{category.name}</span>
                  </button>
                );
              })}
            </div>

            {/* Category Content */}
            <div className="space-y-12">
              {luxuryCategories.map((category) => {
                if (activeCategory !== 'all' && activeCategory !== category.id) return null;
                
                const IconComponent = category.icon;
                const categoryProducts = luxuryProducts[category.id] || [];
                
                return (
                  <div key={category.id} className="bg-white rounded-2xl shadow-lg overflow-hidden">
                    <div className={`bg-gradient-to-r from-${category.color}-600 to-${category.color}-700 p-8`}>
                      <div className="flex items-center space-x-6">
                        <div className="flex-shrink-0">
                          <IconComponent className="w-16 h-16 text-white" />
                        </div>
                        <div className="text-white">
                          <h3 className="text-3xl font-bold mb-2">{category.name}</h3>
                          <p className="text-xl opacity-90 mb-2">{category.description}</p>
                          <p className="text-lg opacity-75">Prisklasse: {category.priceRange}</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="p-8">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {categoryProducts.map((productCategory, index) => (
                          <div key={index} className="border border-gray-200 rounded-xl p-6">
                            <div className="text-center mb-6">
                              <div className="text-4xl mb-3">{productCategory.icon}</div>
                              <h4 className="text-xl font-bold text-gray-900">{productCategory.category}</h4>
                            </div>
                            <ul className="space-y-3">
                              {productCategory.items.map((item, itemIndex) => (
                                <li key={itemIndex} className="flex items-start space-x-3">
                                  <div className="flex-shrink-0 w-2 h-2 bg-gradient-to-r from-purple-400 to-blue-400 rounded-full mt-2"></div>
                                  <span className="text-gray-700 text-sm leading-relaxed">{item}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Occasion-Based Guide */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Luksus-gaver til livets store øyeblikk
              </h2>
              <p className="text-xl text-gray-600">
                Hver anledning fortjener en gave som matcher dens betydning i livet
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {occasionGuides.map((guide, index) => (
                <div key={index} className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-2xl p-8 border border-gray-200">
                  <div className="text-center mb-6">
                    <div className="text-6xl mb-4">{guide.emoji}</div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{guide.occasion}</h3>
                    <p className="text-lg text-purple-600 font-semibold">{guide.budget}</p>
                  </div>
                  
                  <div className="space-y-3">
                    {guide.suggestions.map((suggestion, suggestionIndex) => (
                      <div key={suggestionIndex} className="flex items-center space-x-3 p-3 bg-white rounded-lg">
                        <Star className="w-5 h-5 text-yellow-500 flex-shrink-0" />
                        <span className="text-gray-700">{suggestion}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Strategy & Planning */}
      <section className="py-20 bg-gradient-to-br from-purple-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Strategi for luksus-gavekjøp
              </h2>
              <p className="text-xl text-gray-600">
                På dette nivået krever gavekjøp planlegging, research og strategisk tenkning
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              <div className="space-y-8">
                <div className="bg-white rounded-xl p-8 shadow-lg">
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center">
                      <Shield className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900">Sikkerhet og autentisitet</h3>
                  </div>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                      <span>Kjøp kun fra autoriserte forhandlere med dokumentasjon</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                      <span>Krev autentisitetsbevis og garantier</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                      <span>Research markedspriser og sammenlign tilbud</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                      <span>Vurder forsikring for verdifulle gjenstander</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-white rounded-xl p-8 shadow-lg">
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                      <Eye className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900">Research og planlegging</h3>
                  </div>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                      <span>Start planlegging 3-6 måneder i forveien</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                      <span>Studer mottakerens preferanser og drømmer</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                      <span>Konsulter eksperter innen relevant felt</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                      <span>Ha en backup-plan hvis første valget ikke fungerer</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="space-y-8">
                <div className="bg-white rounded-xl p-8 shadow-lg">
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center">
                      <Clock className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900">Timing og finansiering</h3>
                  </div>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></div>
                      <span>Spar månedlig for å unngå finansielt stress</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></div>
                      <span>Utnytt sesongsvingninger og spesialtilbud</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></div>
                      <span>Vurder 0% finansiering fra seriøse forhandlere</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></div>
                      <span>Husk skattemessige konsekvenser ved store gaver</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-white rounded-xl p-8 shadow-lg">
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg flex items-center justify-center">
                      <Sparkles className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900">Presentasjon og overlevering</h3>
                  </div>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-amber-500 rounded-full mt-2 flex-shrink-0"></div>
                      <span>Investér i profesjonell innpakning og presentasjon</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-amber-500 rounded-full mt-2 flex-shrink-0"></div>
                      <span>Skriv et hjertelig og gjennomtenkt kort</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-amber-500 rounded-full mt-2 flex-shrink-0"></div>
                      <span>Velg det perfekte øyeblikket for overlevering</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-amber-500 rounded-full mt-2 flex-shrink-0"></div>
                      <span>Dokumenter øyeblikket - det blir en del av historien</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Luxury Mindset */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Luksus-filosofien
              </h2>
              <p className="text-xl text-gray-600">
                Forstå den dypere betydningen bak luksus-gaver
              </p>
            </div>

            <div className="space-y-12">
              <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-2xl p-8">
                <div className="text-center mb-8">
                  <Diamond className="w-16 h-16 mx-auto text-purple-600 mb-4" />
                  <h3 className="text-2xl font-bold text-gray-900">Den ultimate luksus-gave</h3>
                </div>
                <p className="text-lg text-gray-700 leading-relaxed text-center max-w-3xl mx-auto">
                  Den perfekte luksus-gaven er ikke nødvendigvis den dyreste, men den som kombinerer 
                  utsøkt kvalitet med perfekt timing og dyp personlig betydning. Det kan være en enkel 
                  gjenstand som betyr alt for akkurat den personen på akkurat det øyeblikket, eller en 
                  storslått opplevelse som oppfyller en livslang drøm. Nøkkelen er at gaven viser du 
                  forstår ikke bare hva de ønsker, men hvem de er i sitt innerste.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="text-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Crown className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Ekstraordinær kvalitet</h3>
                  <p className="text-gray-600">
                    Materialer, håndverk og utførelse som representerer det ypperste innen sitt felt. 
                    Kvalitet som kan føles, sees og verdsettes i tiår fremover.
                  </p>
                </div>

                <div className="text-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-rose-500 to-rose-600 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Heart className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Personlig relevans</h3>
                  <p className="text-gray-600">
                    Gaven må treffe mottakerens sjel - deres drømmer, ønsker eller livs mest 
                    betydningsfulle øyeblikk. Den må føles skreddersydd for akkurat dem.
                  </p>
                </div>

                <div className="text-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-amber-500 to-amber-600 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Award className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Varig påvirkning</h3>
                  <p className="text-gray-600">
                    Luksus-gaver forandrer hvordan mottakeren ser på seg selv og skaper minner 
                    og følelser som varer livet ut. De blir en del av deres historie.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-gradient-to-r from-purple-600 via-blue-600 to-indigo-700 relative overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-20"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Klar til å skape historiens gave?
            </h2>
            <p className="text-xl mb-12 opacity-90 max-w-2xl mx-auto">
              Bruk vår intelligente gavefinner for å oppdage luksus-gaver som matcher både 
              anledningens viktighet og deres unike betydning for deg
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <button
                onClick={() => setCurrentPage('gift-finder')}
                className="px-8 py-4 bg-gradient-to-r from-yellow-400 to-yellow-300 text-gray-900 rounded-xl hover:from-yellow-300 hover:to-yellow-200 transition-all duration-300 transform hover:scale-105 shadow-2xl font-bold text-lg"
              >
                <Gift className="w-6 h-6 inline mr-3" />
                Start luksus-gavefinneren
              </button>
              <button
                onClick={() => setCurrentPage('categories')}
                className="px-8 py-4 border-2 border-white text-white rounded-xl hover:bg-white hover:text-gray-900 transition-all duration-300 font-semibold text-lg"
              >
                Utforsk alle kategorier
              </button>
            </div>

            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
              <div>
                <div className="text-3xl font-bold mb-2">1000+</div>
                <div className="text-sm opacity-75">Kuraterte luksusprodukter</div>
              </div>
              <div>
                <div className="text-3xl font-bold mb-2">50+</div>
                <div className="text-sm opacity-75">Eksklusive merkevarer</div>
              </div>
              <div>
                <div className="text-3xl font-bold mb-2">100%</div>
                <div className="text-sm opacity-75">Autentisitetsgaranti</div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// Add custom CSS for animations
const styles = `
  @keyframes float {
    0%, 100% { transform: translateY(0px) rotate(0deg); }
    50% { transform: translateY(-20px) rotate(5deg); }
  }
  
  @keyframes float-delayed {
    0%, 100% { transform: translateY(0px) rotate(0deg); }
    50% { transform: translateY(-15px) rotate(-3deg); }
  }
  
  .animate-float {
    animation: float 6s ease-in-out infinite;
  }
  
  .animate-float-delayed {
    animation: float-delayed 8s ease-in-out infinite;
    animation-delay: 2s;
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement("style");
  styleSheet.innerText = styles;
  document.head.appendChild(styleSheet);
}

export default Page1000Plus;